import Skeleton from "@components/skeleton/Skeleton";
import { areSetsEqual, composeRefHandlers } from "@utils/general";
import { getRectNormalized } from "@utils/zoom.utils";
import { Dayjs } from "dayjs";
import { debounce, range } from "lodash";
import React, { Component, KeyboardEvent, MouseEvent } from "react";

import { KeyName } from "../../keyName";
import TestIds from "../../testIds";
import { getUtcDayjs } from "../../types/Date";
import FocusManager, { FocusDirection } from "../focusManager";
import { getMonthDays, getMonthFirstDayWeekOffset, getWeekdays, isSameMonth } from "../inputs/date/utils";
import { CalendarWrapper, DayLabel, DayWrapper } from "./Calendar.styles";
import { IDayAction, TileSize } from "./Calendar.utils";
import Day from "./Day";

export interface IProps {
    onDaySelect?: (days: Set<number>, focusedDay: number) => void;
    month?: Dayjs;
    actions?: Record<number, IDayAction[]>;
    actionPlaceholders?: Record<number, IDayAction[]>;
    workedHoursMap?: Record<number, number>;
    holidays?: Date[];
    isSkeleton?: boolean;
    selectedDays?: Set<number>;
    focusedDay?: number;
    firstAvailableDay?: number;
    lastAvailableDay?: number;
}

interface IState {
    selectedDays: Set<number>;
    tileSize: number;
    focusedDay?: number;
}

export default class Calendar extends Component<IProps, IState> {
    _mouseDownOnDay: number;
    _rootDay: number;
    _wrapper = React.createRef<HTMLDivElement>();
    resizeObserver: ResizeObserver;

    state: IState = {
        selectedDays: new Set(),
        tileSize: TileSize.BREAK,
        focusedDay: undefined
    };

    constructor(props: IProps) {
        super(props);

        this.resizeObserver = new ResizeObserver(this.handleResize);
    }

    componentDidMount() {
        if (this.props.isSkeleton) {
            return;
        }

        this.handleResize();
        this.handleResize.flush();
        this.resizeObserver.observe(this._wrapper.current);

        this.setState({
            selectedDays: new Set(this.props.selectedDays),
            focusedDay: this.props.focusedDay
        });
    }

    componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>, snapshot?: any) {
        if (!areSetsEqual(prevProps.selectedDays, this.props.selectedDays)) {
            this.setState({
                selectedDays: new Set(this.props.selectedDays)
            });
        }
        if (prevProps.focusedDay !== this.props.focusedDay) {
            this.setState({
                focusedDay: this.props.focusedDay
            });
        }
    }

    componentWillUnmount() {
        if (this.props.isSkeleton) {
            return;
        }

        this.resizeObserver.disconnect();
        this.handleResize.cancel();
    }

    handleResize = debounce(() => {
        const tileSize = getRectNormalized(this._wrapper.current.children[0]).width;

        this.setState({
            tileSize
        });
    }, 100);

    mouseDownOnDay = debounce((dayNumber: number): void => {
        // debounce to prevent glitch when selecting day changes layout and cursor of the mouse jump to another day, which would select whole range of days
        this._mouseDownOnDay = dayNumber;
    }, 50);

    handleMouseDown = (e: MouseEvent, dayNumber: number): void => {
        if (this.state.focusedDay !== dayNumber) {
            this.mouseDownOnDay(dayNumber);
        }

        this.handleDaySelect(e, dayNumber);
    };

    handleMouseEnter = (e: MouseEvent, dayNumber: number): void => {
        if (this._mouseDownOnDay) {
            this.handleDaySelect(e, dayNumber);
        }
    };

    handleMouseLeave = (e: MouseEvent): void => {
        if (this._mouseDownOnDay) {
            this.handleDaySelect(e, this._mouseDownOnDay);
            this.mouseDownOnDay.cancel();
            this._mouseDownOnDay = null;
        }
    };

    handleMouseUp = (e: MouseEvent): void => {
        if (e.button !== 0) { // only left click
            return;
        }
        this.mouseDownOnDay.cancel();
        this._mouseDownOnDay = null;
    };

    isDirectionKey = (key: string): boolean => {
        return [KeyName.ArrowUp, KeyName.ArrowLeft, KeyName.ArrowDown, KeyName.ArrowRight].includes(key as KeyName);
    };

    getLastDayInSameColumn = (dayNumber: number): number => {
        const weekdayOffset = getMonthFirstDayWeekOffset(this.props.month);
        const daysInMonth = getMonthDays(this.props.month).length;
        const totalDaysWithOffset = daysInMonth + weekdayOffset;

        const columnIndex = (dayNumber + weekdayOffset - 1) % 7;
        const lastRowIndex = Math.floor((totalDaysWithOffset - 1) / 7);
        const lastDayInSameColumn = lastRowIndex * 7 + columnIndex - weekdayOffset + 1;

        return lastDayInSameColumn <= daysInMonth ? lastDayInSameColumn : lastDayInSameColumn - 7;
    };

    getFirstDayInSameColumn = (dayNumber: number): number => {
        const weekdayOffset = getMonthFirstDayWeekOffset(this.props.month);
        const columnIndex = (dayNumber + weekdayOffset - 1) % 7;
        const firstDayInSameColumn = columnIndex - weekdayOffset + 1;

        return firstDayInSameColumn > 0 ? firstDayInSameColumn : firstDayInSameColumn + 7;
    };

    getAdjustedDayNumber = (key: string, dayNumber: number): number => {
        let adjustedDayNumber = dayNumber;
        switch (key) {
            case KeyName.ArrowUp:
                adjustedDayNumber = dayNumber - 7;
                break;
            case KeyName.ArrowLeft:
                adjustedDayNumber = dayNumber - 1;
                break;
            case KeyName.ArrowDown:
                adjustedDayNumber = dayNumber + 7;
                break;
            case KeyName.ArrowRight:
                adjustedDayNumber = dayNumber + 1;
                break;
        }

        if (adjustedDayNumber < 1 || adjustedDayNumber > getMonthDays(this.props.month).length) {
            // adjust moving in grid over the edges of the month, so selections can be made in a grid-like manner
            const daysInMonth = getMonthDays(this.props.month).length;

            if (key === KeyName.ArrowUp && adjustedDayNumber <= 0) {
                adjustedDayNumber = this.getLastDayInSameColumn(dayNumber);
            } else if (key === KeyName.ArrowDown && adjustedDayNumber > daysInMonth) {
                adjustedDayNumber = this.getFirstDayInSameColumn(dayNumber);
            } else if (key === KeyName.ArrowLeft && adjustedDayNumber <= 0) {
                adjustedDayNumber = daysInMonth;
            } else if (key === KeyName.ArrowRight && adjustedDayNumber > daysInMonth) {
                adjustedDayNumber = 1;
            }
        }

        if (adjustedDayNumber < this.props.firstAvailableDay || adjustedDayNumber > this.props.lastAvailableDay) {
            adjustedDayNumber = this.getAdjustedDayNumber(key, adjustedDayNumber);
        }

        return adjustedDayNumber;
    };

    handleKeyDown = (e: KeyboardEvent, dayNumber: number): void => {
        if (this.isDirectionKey(e.key) && e.shiftKey) {
            const adjustedDayNumber = this.getAdjustedDayNumber(e.key, dayNumber);
            e.preventDefault();
            this.handleDaySelect(e, adjustedDayNumber);
        }

        if (e.key === KeyName.Enter) {
            e.preventDefault();
            this.handleDaySelect(e, dayNumber);
        }

        if (e.key === KeyName.Shift && !this._rootDay) {
            e.preventDefault();
            if (!this.state.selectedDays.has(dayNumber)) {
                this.handleDaySelect(e, dayNumber);
            }
            this._rootDay = dayNumber;
        }
    };

    handleKeyUp = (e: KeyboardEvent, dayNumber: number): void => {
        if (e.key === KeyName.Shift) {
            this._rootDay = null;
            e.preventDefault();
        }
    };

    handleDaySelect = (e: MouseEvent | KeyboardEvent, dayNumber: number): void => {
        let selectedDays = this.state.selectedDays;
        let removeFocus = false;

        if ((e.shiftKey && this._rootDay) || (this._mouseDownOnDay && !e.ctrlKey && !e.metaKey && this._mouseDownOnDay !== dayNumber)) {
            const rootDay = e.shiftKey ? this._rootDay : this._mouseDownOnDay;
            const start = Math.min(rootDay, dayNumber);
            const length = Math.abs(rootDay - dayNumber) + 1;
            selectedDays = new Set(Array.from({ length }, (_, i) => start + i));
        } else if (e.ctrlKey || e.metaKey) {
            if (selectedDays.has(dayNumber)) {
                selectedDays.delete(dayNumber);
                removeFocus = true;
            } else {
                selectedDays.add(dayNumber);
            }
        } else {
            if (selectedDays.has(dayNumber) && selectedDays.size === 1) {
                selectedDays.delete(dayNumber);
                removeFocus = true;
            } else {
                selectedDays = new Set([dayNumber]);
            }
        }

        const focusedDay = (selectedDays.size > 0 && !removeFocus) ? dayNumber : null;

        this.props.onDaySelect?.(selectedDays, focusedDay);

        this.setState({
            selectedDays,
            focusedDay
        });
    };

    render() {
        if (this.props.isSkeleton) {
            return <CalendarSkeleton />;
        }

        const currentDayjs = getUtcDayjs();
        const month = this.props.month ?? currentDayjs;
        const weekdays = getWeekdays();
        const weekdayOffset = getMonthFirstDayWeekOffset(month);
        const days = getMonthDays(month);
        let today = 0;

        if (isSameMonth(month.toDate(), currentDayjs.toDate())) {
            today = currentDayjs.date();
        }

        const daysLeft = 7 - (days.length + weekdayOffset) % 7; // days left to complete square

        return <FocusManager direction={FocusDirection.Grid} columnsCount={7}>
            {({ itemProps, wrapperProps }) => (
                <CalendarWrapper
                    {...wrapperProps}
                    data-testid={TestIds.Calendar}
                    onMouseLeave={this.handleMouseLeave}
                    onMouseUp={this.handleMouseUp}
                    ref={composeRefHandlers(this._wrapper, wrapperProps.ref)}
                >
                    {weekdays.map((day, index) => <DayLabel key={day + index}>{day}</DayLabel>)}
                    {range(weekdayOffset).map((index) => <DayWrapper
                        data-testid={TestIds.CalendarDay}
                        key={"pre_" + index}
                        $isDummy={true}
                        data-focusable-item-disabled={true}
                        {...itemProps}
                    />)}

                    {days.map((day, index) => {
                        const dayNumber = index + 1;
                        const isDisabled = (this.props.lastAvailableDay && day.get("date") > this.props.lastAvailableDay)
                            || (this.props.firstAvailableDay && day.get("date") < this.props.firstAvailableDay);
                        return (
                            <Day actions={this.props.actions?.[dayNumber] ?? []}
                                 placeholders={this.props.actionPlaceholders?.[dayNumber]}
                                 workedMinutes={this.props.workedHoursMap?.[dayNumber] ?? 0}
                                 size={this.state.tileSize}
                                 key={dayNumber}
                                 focusableProps={itemProps}
                                 isMonday={day.get("day") === 1}
                                 isToday={today === dayNumber}
                                 isDisabled={isDisabled}
                                 isFocused={this.state.focusedDay === dayNumber}
                                 isSelected={this.state.selectedDays.has(dayNumber)}
                                 isHoliday={this.props.holidays?.some(h => getUtcDayjs(h).date() === dayNumber)}
                                 dayNumber={dayNumber}
                                 onMouseDown={this.handleMouseDown}
                                 onMouseEnter={this.handleMouseEnter}
                                 onMouseUp={this.handleMouseUp}
                                 onKeyDown={this.handleKeyDown}
                                 onKeyUp={this.handleKeyUp}
                            />
                        );
                    })}

                    {range(daysLeft === 7 ? 0 : daysLeft).map((index) => <DayWrapper
                        data-testid={TestIds.CalendarDay}
                        key={"post_" + index}
                        $isDummy={true}
                        data-focusable-item-disabled={true}
                        {...itemProps}
                    />)}
                </CalendarWrapper>
            )}
        </FocusManager>;
    }
}


const CalendarSkeleton: React.FunctionComponent = () => {
    const numOfTiles = 7 * 5;

    return (
        <div style={{
            display: "grid",
            gap: "2px",
            gridTemplateColumns: "repeat(7, 1fr)",
            gridTemplateRows: "30px repeat(5, 1fr)",
            height: "fit-content",
            justifyItems: "center"
        }}>
            {range(7).map((index) => (
                <Skeleton width={"30px"} height={"22px"} />
            ))}
            {range(numOfTiles).map((index) => (
                <Skeleton width={"104px"} height={"104px"} />
            ))}
        </div>
    );
};